/**
 * CatalogAssetPicker — modal for browsing and selecting catalog assets.
 *
 * Requires the user to be logged into the CMS (catalog auth token).
 * Shows a grid of assets filtered by EntityType (Animation or Material),
 * with thumbnails, names, and a click-to-select interaction.
 *
 * Rendered via a fixed-position overlay (no portal needed — sandbox renders
 * inside a single root so z-index is sufficient).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Assets, EntityType } from 'trace-viewer/cms';
import type { AssetResponseDto } from 'trace-viewer/cms';
import { apiConfig, resolveUrl } from '../catalog-shared';
import { ensureFreshToken } from '../catalog-auth';

// ============================================================================
// Types
// ============================================================================

export interface CatalogAsset {
    /** Asset ID in the CMS */
    assetId: string;
    title: string;
    /** Resolved absolute URL to the asset file */
    url: string;
    thumbnailUrl?: string;
    fileType?: string;
}

export interface CatalogAssetPickerProps {
    /** Which entity type to list */
    entityType: EntityType.Animation | EntityType.Material;
    /** Called with the selected asset URL (resolved absolute) */
    onSelect: (asset: CatalogAsset) => void;
    /** Called when the user dismisses without selecting */
    onClose: () => void;
}

// ============================================================================
// CatalogAssetPicker
// ============================================================================

export const CatalogAssetPicker: React.FC<CatalogAssetPickerProps> = ({
    entityType,
    onSelect,
    onClose,
}) => {
    const [assets, setAssets] = useState<AssetResponseDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const backdropRef = useRef<HTMLDivElement>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await ensureFreshToken();
            if (!token) {
                setError('Not signed in. Please log in via the Catalog panel.');
                return;
            }
            const client = new Assets(apiConfig(token));
            const result = await client.assetsList({ Kind: entityType });
            setAssets(result.data ?? []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load assets.');
        } finally {
            setLoading(false);
        }
    }, [entityType]);

    useEffect(() => { void load(); }, [load]);

    // Close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    const filtered = search.trim()
        ? assets.filter(a =>
            (a.title ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (a.originalFilename ?? '').toLowerCase().includes(search.toLowerCase())
        )
        : assets;

    const label = entityType === EntityType.Animation ? 'Animations' : 'Materials';

    return (
        <>
            {/* Backdrop */}
            <div
                ref={backdropRef}
                onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {/* Modal */}
                <div style={{
                    backgroundColor: '#1e1e1e',
                    border: '1px solid #444',
                    borderRadius: '6px',
                    width: '480px',
                    maxWidth: '90vw',
                    maxHeight: '70vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 14px',
                        borderBottom: '1px solid #333',
                        gap: '10px',
                        flexShrink: 0,
                    }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#ccc', flex: 1 }}>
                            Catalog — {label}
                        </span>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none', border: 'none', color: '#888',
                                cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '2px 4px',
                            }}
                            title="Close"
                        >
                            ×
                        </button>
                    </div>

                    {/* Search */}
                    <div style={{ padding: '8px 14px', flexShrink: 0, borderBottom: '1px solid #2a2a2a' }}>
                        <input
                            type="text"
                            placeholder={`Search ${label.toLowerCase()}...`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '5px 8px',
                                backgroundColor: '#2a2a2a',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                color: '#ddd',
                                fontSize: '12px',
                                outline: 'none',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    {/* Asset list */}
                    <div style={{ overflowY: 'auto', flex: 1, padding: '8px 10px' }}>
                        {loading && (
                            <div style={{ color: '#666', fontSize: '12px', textAlign: 'center', padding: '24px' }}>
                                Loading...
                            </div>
                        )}
                        {error && (
                            <div style={{ color: '#e06c6c', fontSize: '12px', padding: '12px' }}>
                                {error}
                            </div>
                        )}
                        {!loading && !error && filtered.length === 0 && (
                            <div style={{ color: '#666', fontSize: '12px', textAlign: 'center', padding: '24px' }}>
                                {assets.length === 0 ? `No ${label.toLowerCase()} in your catalog.` : 'No results match.'}
                            </div>
                        )}
                        {!loading && !error && filtered.map((asset) => {
                            const resolvedUrl = resolveUrl(asset.url);
                            if (!resolvedUrl || !asset.assetId) return null;
                            const thumbUrl = resolveUrl(asset.thumbnailUrl);
                            const isHovered = hoveredId === asset.assetId;
                            return (
                                <div
                                    key={asset.assetId}
                                    onClick={() => {
                                        onSelect({
                                            assetId: asset.assetId!,
                                            title: asset.title ?? asset.originalFilename ?? 'Untitled',
                                            url: resolvedUrl,
                                            thumbnailUrl: thumbUrl,
                                            fileType: asset.fileType ?? undefined,
                                        });
                                    }}
                                    onMouseEnter={() => setHoveredId(asset.assetId!)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '6px 8px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        backgroundColor: isHovered ? '#2e2e2e' : 'transparent',
                                        transition: 'background-color 0.1s',
                                        marginBottom: '2px',
                                    }}
                                >
                                    {/* Thumbnail */}
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '3px',
                                        backgroundColor: '#111',
                                        border: '1px solid #333',
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        {thumbUrl ? (
                                            <img
                                                src={thumbUrl}
                                                alt=""
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            />
                                        ) : (
                                            <span style={{ fontSize: '18px', color: '#444' }}>
                                                {entityType === EntityType.Animation ? '▶' : '◈'}
                                            </span>
                                        )}
                                    </div>

                                    {/* Name + file type */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#ddd',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {asset.title ?? asset.originalFilename ?? 'Untitled'}
                                        </div>
                                        {asset.fileType && (
                                            <div style={{ fontSize: '10px', color: '#666', marginTop: '1px' }}>
                                                {asset.fileType}
                                            </div>
                                        )}
                                    </div>

                                    {/* Select hint */}
                                    {isHovered && (
                                        <span style={{ fontSize: '10px', color: '#7ab', flexShrink: 0 }}>
                                            Select →
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

// ============================================================================
// Catalog browse button — small icon button to open the picker
// ============================================================================

export interface CatalogBrowseButtonProps {
    entityType: EntityType.Animation | EntityType.Material;
    onSelect: (asset: CatalogAsset) => void;
    title?: string;
}

export const CatalogBrowseButton: React.FC<CatalogBrowseButtonProps> = ({
    entityType,
    onSelect,
    title,
}) => {
    const [open, setOpen] = useState(false);

    const handleSelect = useCallback((asset: CatalogAsset) => {
        onSelect(asset);
        setOpen(false);
    }, [onSelect]);

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                title={title ?? `Browse catalog ${entityType === EntityType.Animation ? 'animations' : 'materials'}`}
                style={{
                    padding: '2px 5px',
                    backgroundColor: 'transparent',
                    border: '1px solid #444',
                    borderRadius: '3px',
                    color: '#7ab',
                    cursor: 'pointer',
                    fontSize: '12px',
                    lineHeight: 1,
                    flexShrink: 0,
                }}
            >
                ⊞
            </button>
            {open && (
                <CatalogAssetPicker
                    entityType={entityType}
                    onSelect={handleSelect}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
};
